// generateWebSiteJsonLd.js
import { getCoporation } from '../resource/SchemaObject.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { DEFAULT_LANGUAGE } from '../resource/i18nCommon.js';
import { normalizeLang, getHreflang } from '../resource/langPath.js';

/**
 * Generate WebSite JSON-LD for homepage with multi-language support
 * This function is designed for Next.js RRv7 server-side rendering
 *
 * @param {Object} params
 * @param {Object} params.state - Redux state object
 * @param {Object} params.fullUrl - Full URL object with origin
 * @param {string} params.titleKey - Translation key for title (default: 'seo-home-title')
 * @param {string} params.descriptionKey - Translation key for description
 * @param {Object} params.i18n - i18n instance (required)
 * @returns {Promise<Object>} JSON-LD object with WebSite, WebPage, and BreadcrumbList
 */
export const generateWebSiteJsonLd = async ({
  fullUrl,
  titleKey = 'seo-home-title',
  descriptionKey = 'seo-explore-description',
  i18n,
}) => {
  const t = i18n.t;
  let { origin } = fullUrl;

  const currentLanguage = i18n.language;
  const langPath =
    normalizeLang(currentLanguage) === DEFAULT_LANGUAGE
      ? ''
      : `/${normalizeLang(currentLanguage)}`;
  const originWithLang = `${origin}${langPath}`;

  const normalizedInLanguage = getHreflang(currentLanguage);

  const loadLanguagePromise = async () => {
    if (!i18n.hasResourceBundle(currentLanguage, TranslationNamespace.SEO)) {
      await i18n.reloadResources(currentLanguage, TranslationNamespace.SEO);
    }
  };
  await loadLanguagePromise();

  // Helper function to get translated title by language
  const getTitleByLang = lang =>
    t(titleKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  // Helper function to get translated description by language
  const getDescriptionByLang = lang =>
    t(descriptionKey, {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  const buildNameValueByLang = (lang, translateFn) => translateFn(lang);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      getCoporation({ origin, langPath }),
      {
        '@type': 'WebSite',
        '@id': `${originWithLang}/#website`,
        url: originWithLang,
        inLanguage: normalizedInLanguage,
        name: 'SWAG',
        description:
          buildNameValueByLang(currentLanguage, getDescriptionByLang) ||
          buildNameValueByLang(currentLanguage, getTitleByLang),
        image: {
          '@id': `${originWithLang}/#logo`,
        },
        mainEntity: {
          '@id': `${originWithLang}/#webpage`,
        },
        // publisher prop will lead google not recogonize logo in Corporation
        // publisher: {
        //   '@id': 'https://swag.live/#corporation',
        // },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${originWithLang}/search/all?q={search_term}`,
          },
          'query-input': 'required name=search_term',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${originWithLang}/#webpage`,
        url: originWithLang,
        name: buildNameValueByLang(currentLanguage, getTitleByLang),
        breadcrumb: {
          '@id': `${originWithLang}/#breadcrumb`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${originWithLang}/#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: buildNameValueByLang(currentLanguage, getTitleByLang),
            item: {
              '@id': `${originWithLang}/#website`,
            },
          },
        ],
      },
    ],
  };
};

export default generateWebSiteJsonLd;
